import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star, ArrowLeft, CircleNotch, Target, ArrowUp, ArrowDown, Minus,
  Fire, Snowflake, TrendUp, TrendDown, Equals,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/* ============== STATUS BADGE ============== */
const StatusBadge = ({ status }) => {
  const map = {
    HOT: 'bg-red-100 text-red-700 border-red-300',
    COLD: 'bg-blue-100 text-blue-700 border-blue-300',
    HEATING: 'bg-orange-100 text-orange-700 border-orange-300',
    COOLING: 'bg-cyan-100 text-cyan-700 border-cyan-300',
    NEUTRAL: 'bg-gray-100 text-gray-600 border-gray-300',
    MIXED: 'bg-purple-100 text-purple-700 border-purple-300',
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${map[status] || map.NEUTRAL}`}>{status}</span>;
};

const TrendIcon = ({ trend }) => {
  if (trend === 'UP') return <ArrowUp size={14} weight="bold" className="text-green-600" />;
  if (trend === 'DOWN') return <ArrowDown size={14} weight="bold" className="text-red-600" />;
  return <Minus size={12} className="text-gray-400" />;
};

/* ============== TAB 1: GAP & OVERDUE ============== */
const GapOverdueTab = ({ data }) => {
  const [subTab, setSubTab] = useState('combined');
  const balls = data.balls || {};
  const bd = balls[subTab] || {};
  const nums = bd.numbers || [];
  const predicted = data.predicted || [];

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="bg-white border border-gray-200 p-1">
          <TabsTrigger value="combined" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="gap-tab-combined">Combined</TabsTrigger>
          <TabsTrigger value="lucky_star_1" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="gap-tab-ls1">LS1</TabsTrigger>
          <TabsTrigger value="lucky_star_2" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="gap-tab-ls2">LS2</TabsTrigger>
        </TabsList>

        {['combined', 'lucky_star_1', 'lucky_star_2'].map(key => {
          const d = balls[key] || {};
          const n = d.numbers || [];
          return (
            <TabsContent key={key} value={key}>
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>{d.label} — Gap & Overdue</h3>
                  <p className="text-xs text-gray-500 mt-1">Numbers sorted by gap ratio (highest = most overdue). Gap Ratio {'>'} 1.0 means the number is statistically overdue.</p>
                </div>
                <div className="overflow-x-auto" data-testid="gap-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-700">
                        <th className="px-2 py-2 text-center">#</th>
                        <th className="px-2 py-2 text-center bg-gray-200">Apps</th>
                        <th className="px-2 py-2 text-center bg-red-100">Cur Gap</th>
                        <th className="px-2 py-2 text-center">Avg Gap</th>
                        <th className="px-2 py-2 text-center">Med Gap</th>
                        <th className="px-2 py-2 text-center">Max Gap</th>
                        <th className="px-2 py-2 text-center">Min Gap</th>
                        <th className="px-2 py-2 text-center bg-amber-100">Gap Ratio</th>
                        <th className="px-2 py-2 text-center bg-rose-100">Score</th>
                        <th className="px-2 py-2 text-center" style={{ minWidth: '160px' }}>Overdue Meter</th>
                      </tr>
                    </thead>
                    <tbody>
                      {n.map((row, idx) => {
                        const ratio = row.gap_ratio || 0;
                        const barW = Math.min(100, ratio * 40);
                        const barColor = ratio >= 2 ? 'bg-red-500' : ratio >= 1.5 ? 'bg-orange-500' : ratio >= 1 ? 'bg-amber-400' : 'bg-green-400';
                        if (row.is_impossible) {
                          return (
                            <tr key={row.number} className="bg-gray-100 opacity-60" data-testid={`gap-row-${row.number}`} title={`Impossible: ${key === 'lucky_star_1' ? 'LS1 cannot be 12' : 'LS2 cannot be 1'} (LS stored sorted: LS1 < LS2)`}>
                              <td className="px-2 py-1.5 border-b border-gray-200 font-mono font-bold text-center text-sm line-through text-gray-400">{row.number}</td>
                              <td colSpan={8} className="px-2 py-1.5 border-b border-gray-200 text-center text-[11px] text-rose-600 font-bold italic">
                                ⛔ Impossible — {key === 'lucky_star_1' ? 'LS1 max is 11 (LS1 < LS2)' : 'LS2 min is 2 (LS1 < LS2)'}
                              </td>
                              <td className="px-2 py-1.5 border-b border-gray-200" />
                            </tr>
                          );
                        }
                        return (
                          <tr key={row.number} className={ratio >= 1.5 ? 'bg-red-50' : ratio >= 1 ? 'bg-amber-50/30' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`gap-row-${row.number}`}>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono font-bold text-center text-sm">{row.number}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-gray-50 font-bold">{row.total_appearances}</td>
                            <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold ${ratio >= 1.5 ? 'text-red-600 bg-red-50' : ratio >= 1 ? 'text-amber-600' : ''}`}>{row.current_gap}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{row.avg_gap}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{row.median_gap}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{row.max_gap}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{row.min_gap}</td>
                            <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold text-sm ${ratio >= 2 ? 'text-red-700' : ratio >= 1.5 ? 'text-orange-600' : ratio >= 1 ? 'text-amber-600' : 'text-green-600'}`}>{ratio}x</td>
                            <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold ${row.overdue_score >= 60 ? 'text-red-700' : row.overdue_score >= 30 ? 'text-amber-600' : 'text-green-600'}`}>{row.overdue_score}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100">
                              <div className="w-full bg-gray-200 rounded-full h-3 relative">
                                <div className={`${barColor} h-3 rounded-full transition-all`} style={{ width: `${barW}%` }} />
                                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-gray-800">{ratio}x</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Prediction */}
      <div className="bg-gradient-to-r from-red-700 to-orange-800 rounded-lg p-5 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Target size={24} weight="bold" />
          <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>Most Overdue Lucky Stars (Combined)</h3>
        </div>
        <p className="text-xs text-white/70 mb-4">Numbers with gap ratio {'>'} 1.0 are overdue — they haven't appeared as frequently as their historical average suggests.</p>
        <div className="grid grid-cols-5 gap-3">
          {predicted.map((p, idx) => (
            <div key={p.number} className={`rounded-lg p-3 text-center ${idx === 0 ? 'bg-white/25' : 'bg-white/10'}`} data-testid={`gap-pred-${p.number}`}>
              <div className="text-3xl font-bold font-mono">{p.number}</div>
              <div className={`text-sm font-bold mt-1 ${p.gap_ratio >= 2 ? 'text-yellow-300' : p.gap_ratio >= 1 ? 'text-orange-200' : 'text-white/60'}`}>{p.gap_ratio}x overdue</div>
              <div className="text-[10px] text-white/60 mt-1">Gap: {p.current_gap} | Avg: {p.avg_gap}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ============== TAB 2: MARKOV CHAIN ============== */
const MarkovTab = ({ data }) => {
  const [subTab, setSubTab] = useState('lucky_star_1');
  const balls = data.balls || {};

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="bg-white border border-gray-200 p-1">
          <TabsTrigger value="lucky_star_1" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="markov-tab-ls1">Ball 6 (LS1)</TabsTrigger>
          <TabsTrigger value="lucky_star_2" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="markov-tab-ls2">Ball 7 (LS2)</TabsTrigger>
        </TabsList>

        {['lucky_star_1', 'lucky_star_2'].map(bk => {
          const bd = balls[bk] || {};
          const matrix = bd.matrix || [];
          const preds = bd.predictions || [];
          const hot = bd.hot_transitions || [];
          const lastVal = bd.last_value;

          return (
            <TabsContent key={bk} value={bk}>
              {/* Current state + predictions */}
              <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 text-white mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <Target size={24} weight="bold" />
                  <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
                    {bd.label} — Last Drawn: <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-400 text-gray-900 text-xl font-bold ml-2">{lastVal}</span>
                  </h3>
                </div>
                <p className="text-xs text-white/70 mb-4">Based on {bd.total_transitions} historical transitions. After drawing {lastVal}, the most likely next values are:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="markov-prediction-table">
                    <thead><tr className="text-white/70 border-b border-white/20">
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-center">Number</th>
                      <th className="px-2 py-2 text-center">Times</th>
                      <th className="px-2 py-2 text-center">Probability</th>
                      <th className="px-2 py-2 text-center">vs Expected</th>
                      <th className="px-2 py-2 text-center" style={{ minWidth: '200px' }}>Strength</th>
                    </tr></thead>
                    <tbody>
                      {preds.map((p, idx) => {
                        const barW = Math.min(100, p.pct * 4);
                        return (
                          <tr key={p.number} className={idx === 0 ? 'bg-white/20' : idx % 2 === 0 ? 'bg-white/5' : ''}>
                            <td className="px-2 py-2 font-bold">{idx + 1}</td>
                            <td className="px-2 py-2 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-gray-900 font-bold text-lg">{p.number}</span>
                            </td>
                            <td className="px-2 py-2 text-center font-mono font-bold">{p.count}</td>
                            <td className="px-2 py-2 text-center font-mono font-bold text-lg">{p.pct}%</td>
                            <td className={`px-2 py-2 text-center font-mono font-bold ${p.deviation > 0 ? 'text-green-300' : p.deviation < 0 ? 'text-red-300' : ''}`}>
                              {p.deviation > 0 ? '+' : ''}{p.deviation}%
                            </td>
                            <td className="px-2 py-2">
                              <div className="w-full bg-white/10 rounded-full h-3">
                                <div className={`h-3 rounded-full ${p.deviation > 3 ? 'bg-green-400' : p.deviation > 0 ? 'bg-blue-300' : 'bg-gray-400'}`} style={{ width: `${barW}%` }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Transition Matrix Heatmap */}
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>{bd.label} — 12x12 Transition Matrix</h3>
                  <p className="text-xs text-gray-500 mt-1">P(next = column | current = row). Expected ~8.3% per cell. Green = above expected, Red = below.</p>
                </div>
                <div className="overflow-x-auto p-2" data-testid="markov-matrix">
                  <table className="text-[10px] mx-auto" style={{ borderCollapse: 'separate', borderSpacing: '1px' }}>
                    <thead>
                      <tr>
                        <th className="px-2 py-1 bg-gray-800 text-white rounded-tl font-bold">From\To</th>
                        {[...Array(12)].map((_, i) => (
                          <th key={`th-${i + 1}`} className={`px-2 py-1 font-bold text-center ${lastVal === i + 1 ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-white'}`}>{i + 1}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.map(row => (
                        <tr key={row.from_number}>
                          <td className={`px-2 py-1 font-bold text-center ${lastVal === row.from_number ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-white'}`}>{row.from_number}</td>
                          {row.transitions.map(t => {
                            const intensity = Math.min(1, Math.abs(t.deviation) / 8);
                            const bg = t.deviation > 0
                              ? `rgba(34,197,94,${0.1 + intensity * 0.6})`
                              : t.deviation < 0
                                ? `rgba(239,68,68,${0.1 + intensity * 0.4})`
                                : 'rgba(229,231,235,0.3)';
                            const isHighlight = lastVal === row.from_number;
                            return (
                              <td key={t.to_number}
                                className={`px-1.5 py-1 text-center font-mono ${isHighlight ? 'font-bold' : ''}`}
                                style={{ backgroundColor: bg, minWidth: '36px' }}
                                title={`${row.from_number} -> ${t.to_number}: ${t.count}x (${t.pct}%)`}
                              >
                                {t.pct}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Hot Transitions */}
              {hot.length > 0 && (
                <div className="bg-white border border-gray-200 rounded p-4 mt-4">
                  <h3 className="font-bold text-gray-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Hot Transitions ({'>'}12% probability)</h3>
                  <div className="flex flex-wrap gap-2">
                    {hot.map((h, idx) => (
                      <div key={`hot-${h.from}-${h.to}-${idx}`} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1 text-xs">
                        <span className="font-mono font-bold">{h.from}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-mono font-bold">{h.to}</span>
                        <span className="text-green-700 font-bold ml-1">{h.pct}%</span>
                        <span className="text-green-600 text-[10px]">(+{h.deviation}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

/* ============== TAB 3: HOT/COLD CYCLES ============== */
const HotColdTab = ({ data }) => {
  const [subTab, setSubTab] = useState('combined');
  const balls = data.balls || {};

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="bg-white border border-gray-200 p-1">
          <TabsTrigger value="combined" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="hc-tab-combined">Combined</TabsTrigger>
          <TabsTrigger value="lucky_star_1" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="hc-tab-ls1">LS1</TabsTrigger>
          <TabsTrigger value="lucky_star_2" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="hc-tab-ls2">LS2</TabsTrigger>
        </TabsList>

        {['combined', 'lucky_star_1', 'lucky_star_2'].map(key => {
          const bd = balls[key] || {};
          const nums = bd.numbers || [];
          return (
            <TabsContent key={key} value={key}>
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>{bd.label} — Hot/Cold Rolling Cycles</h3>
                  <p className="text-xs text-gray-500 mt-1">Compares appearance rate in last 10, 20, 50 draws vs overall. <strong>HEATING</strong> = recently hot but historically normal (most predictive for upcoming draws).</p>
                </div>
                <div className="overflow-x-auto" data-testid="hc-table">
                  <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '800px' }}>
                    <thead className="bg-gray-100">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-700">
                        <th className="px-2 py-2 text-center" style={{ width: '40px' }}>#</th>
                        <th className="px-2 py-2 text-center bg-gray-200" style={{ width: '50px' }}>Apps</th>
                        <th className="px-2 py-2 text-center" style={{ width: '50px' }}>Ovr%</th>
                        <th className="px-2 py-2 text-center bg-red-100" style={{ width: '50px' }}>L10%</th>
                        <th className="px-2 py-2 text-center bg-red-50" style={{ width: '45px' }}>Diff</th>
                        <th className="px-2 py-2 text-center bg-orange-100" style={{ width: '50px' }}>L20%</th>
                        <th className="px-2 py-2 text-center bg-blue-100" style={{ width: '50px' }}>L50%</th>
                        <th className="px-2 py-2 text-center bg-amber-100" style={{ width: '75px' }}>Status</th>
                        <th className="px-2 py-2 text-center" style={{ width: '40px' }}>Trend</th>
                        <th className="px-2 py-2 text-center bg-purple-100" style={{ width: '65px' }}>Streak</th>
                        <th className="px-2 py-2 text-center" style={{ width: '140px' }}>Heat Map</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nums.map((row, idx) => {
                        const w = row.windows || {};
                        const l10 = w.last_10 || {};
                        const l20 = w.last_20 || {};
                        const l50 = w.last_50 || {};
                        const heatVal = l10.diff || 0;
                        const barW = Math.min(100, Math.abs(heatVal) * 8);
                        const barDir = heatVal >= 0 ? 'right' : 'left';
                        return (
                          <tr key={row.number} className={row.status === 'HOT' ? 'bg-red-50' : row.status === 'COLD' ? 'bg-blue-50' : row.status === 'HEATING' ? 'bg-orange-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`hc-row-${row.number}`}>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono font-bold text-center text-sm">{row.number}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-gray-50">{row.overall_count}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center text-[10px]">{row.overall_rate}%</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-red-50/50">{l10.rate}%</td>
                            <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${l10.diff > 0 ? 'text-green-700' : l10.diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>{l10.diff > 0 ? '+' : ''}{l10.diff}</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-orange-50/30">{l20.rate}%</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{l50.rate}%</td>
                            <td className="px-2 py-1.5 border-b border-gray-100 text-center"><StatusBadge status={row.status} /></td>
                            <td className="px-2 py-1.5 border-b border-gray-100 text-center"><TrendIcon trend={row.trend} /></td>
                            <td className="px-2 py-1.5 border-b border-gray-100 text-center text-[10px] font-mono">
                              <span className={`font-bold ${row.streak_type === 'hit' ? 'text-green-700' : 'text-red-600'}`}>
                                {row.streak_count} {row.streak_type === 'hit' ? 'hits' : 'miss'}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 border-b border-gray-100">
                              <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-400 z-10" />
                                {heatVal >= 0 ? (
                                  <div className="absolute top-0 bottom-0 left-1/2 bg-red-400 rounded-r" style={{ width: `${barW / 2}%` }} />
                                ) : (
                                  <div className="absolute top-0 bottom-0 bg-blue-400 rounded-l" style={{ width: `${barW / 2}%`, right: '50%' }} />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {nums.filter(n => n.status === 'HOT' || n.status === 'HEATING').slice(0, 4).map(n => (
                  <div key={n.number} className={`rounded-lg p-3 border ${n.status === 'HOT' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                    <div className="flex items-center gap-2">
                      <Fire size={16} weight="fill" className={n.status === 'HOT' ? 'text-red-500' : 'text-orange-500'} />
                      <span className="font-mono text-xl font-bold">{n.number}</span>
                      <StatusBadge status={n.status} />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">L10: {n.windows?.last_10?.rate}% vs Ovr: {n.overall_rate}%</div>
                  </div>
                ))}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

/* ============== TAB 4: SUM & SPREAD ============== */
const SumSpreadTab = ({ data }) => {
  const [subTab, setSubTab] = useState('sums');
  const sums = data.sums || [];
  const spreads = data.spreads || [];
  const maxSumCount = Math.max(...sums.map(s => s.count), 1);
  const maxSpreadCount = Math.max(...spreads.map(s => s.count), 1);

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded p-4">
          <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Avg Sum (LS1+LS2)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">{data.avg_sum}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded p-4">
          <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Avg Spread (LS2-LS1)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">{data.avg_spread}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded p-4 bg-amber-50">
          <p className="text-xs uppercase tracking-wider font-bold text-amber-600">Last Sum</p>
          <p className="text-2xl font-bold text-amber-700 mt-1 font-mono">{data.last_sum}</p>
        </div>
        <div className="bg-white border border-blue-200 rounded p-4 bg-blue-50">
          <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Last Spread</p>
          <p className="text-2xl font-bold text-blue-700 mt-1 font-mono">{data.last_spread}</p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="bg-white border border-gray-200 p-1">
          <TabsTrigger value="sums" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="ss-tab-sums">Sums (LS1+LS2)</TabsTrigger>
          <TabsTrigger value="spreads" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold text-xs" data-testid="ss-tab-spreads">Spreads (LS2-LS1)</TabsTrigger>
        </TabsList>

        <TabsContent value="sums">
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Sum Distribution (LS1 + LS2)</h3>
              <p className="text-xs text-gray-500 mt-1">Range: 3 (1+2) to 23 (11+12). Top sums: <strong>{data.top_sums?.join(', ')}</strong></p>
            </div>
            <div className="overflow-x-auto" data-testid="sum-table">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-700">
                    <th className="px-2 py-2 text-center">Sum</th>
                    <th className="px-2 py-2 text-center bg-gray-200">Count</th>
                    <th className="px-2 py-2 text-center">%</th>
                    <th className="px-2 py-2 text-center bg-amber-100">{data.current_month_abbr}</th>
                    <th className="px-2 py-2 text-center bg-green-100">{data.current_year}</th>
                    <th className="px-2 py-2 text-center bg-red-100">L10</th>
                    <th className="px-2 py-2 text-center bg-orange-100">L20</th>
                    <th className="px-2 py-2 text-center bg-blue-100">L50</th>
                    <th className="px-2 py-2 text-center bg-purple-100">Trend</th>
                    <th className="px-2 py-2 text-center" style={{ minWidth: '180px' }}>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {sums.map((row, idx) => {
                    const isTop = data.top_sums?.includes(row.value);
                    const barW = (row.count / maxSumCount) * 100;
                    return (
                      <tr key={row.value} className={isTop ? 'bg-amber-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`sum-row-${row.value}`}>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center text-sm ${isTop ? 'font-bold text-amber-700' : 'font-bold'}`}>{row.value}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-gray-50">{row.count}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{row.pct}%</td>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/40 ${row.month_count > 0 ? 'font-bold' : 'text-gray-300'}`}>{row.month_count || '-'}</td>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-green-50/30 ${row.cy_count > 0 ? 'font-bold' : 'text-gray-300'}`}>{row.cy_count || '-'}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-red-50/30 font-bold">{row.last_10}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-orange-50/30">{row.last_20}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{row.last_50}</td>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.trend_diff > 0 ? 'text-green-700' : row.trend_diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {row.trend_diff > 0 ? '+' : ''}{row.trend_diff}
                        </td>
                        <td className="px-2 py-1.5 border-b border-gray-100">
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${isTop ? 'bg-amber-400' : 'bg-blue-300'}`} style={{ width: `${barW}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="spreads">
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Spread Distribution (LS2 - LS1)</h3>
              <p className="text-xs text-gray-500 mt-1">Range: 1 to 11. Top spreads: <strong>{data.top_spreads?.join(', ')}</strong></p>
            </div>
            <div className="overflow-x-auto" data-testid="spread-table">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-700">
                    <th className="px-2 py-2 text-center">Spread</th>
                    <th className="px-2 py-2 text-center bg-gray-200">Count</th>
                    <th className="px-2 py-2 text-center">%</th>
                    <th className="px-2 py-2 text-center bg-amber-100">{data.current_month_abbr}</th>
                    <th className="px-2 py-2 text-center bg-green-100">{data.current_year}</th>
                    <th className="px-2 py-2 text-center bg-red-100">L10</th>
                    <th className="px-2 py-2 text-center bg-orange-100">L20</th>
                    <th className="px-2 py-2 text-center bg-blue-100">L50</th>
                    <th className="px-2 py-2 text-center bg-purple-100">Trend</th>
                    <th className="px-2 py-2 text-center" style={{ minWidth: '180px' }}>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {spreads.map((row, idx) => {
                    const isTop = data.top_spreads?.includes(row.value);
                    const barW = (row.count / maxSpreadCount) * 100;
                    return (
                      <tr key={row.value} className={isTop ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`spread-row-${row.value}`}>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center text-sm ${isTop ? 'font-bold text-blue-700' : 'font-bold'}`}>{row.value}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center font-bold bg-gray-50">{row.count}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center">{row.pct}%</td>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-amber-50/40 ${row.month_count > 0 ? 'font-bold' : 'text-gray-300'}`}>{row.month_count || '-'}</td>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-green-50/30 ${row.cy_count > 0 ? 'font-bold' : 'text-gray-300'}`}>{row.cy_count || '-'}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-red-50/30 font-bold">{row.last_10}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-orange-50/30">{row.last_20}</td>
                        <td className="px-2 py-1.5 border-b border-gray-100 font-mono text-center bg-blue-50/30">{row.last_50}</td>
                        <td className={`px-2 py-1.5 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.trend_diff > 0 ? 'text-green-700' : row.trend_diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {row.trend_diff > 0 ? '+' : ''}{row.trend_diff}
                        </td>
                        <td className="px-2 py-1.5 border-b border-gray-100">
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${isTop ? 'bg-blue-400' : 'bg-gray-300'}`} style={{ width: `${barW}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ============== MAIN PAGE ============== */
export default function WindowLSAdvanced() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('gap');
  const [loading, setLoading] = useState(true);
  const [gapData, setGapData] = useState(null);
  const [markovData, setMarkovData] = useState(null);
  const [hotColdData, setHotColdData] = useState(null);
  const [sumSpreadData, setSumSpreadData] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [g, m, h, s] = await Promise.all([
        axios.get(`${API}/analysis/ls-gap-overdue`),
        axios.get(`${API}/analysis/ls-markov`),
        axios.get(`${API}/analysis/ls-hot-cold`),
        axios.get(`${API}/analysis/ls-sum-spread`),
      ]);
      setGapData(g.data);
      setMarkovData(m.data);
      setHotColdData(h.data);
      setSumSpreadData(s.data);
    } catch {
      toast.error('Failed to fetch LS advanced analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700" data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>W37-40: LS Advanced Analytics</h1>
                <p className="text-xs text-gray-500">Windows 39-42: Gap, Markov, Hot/Cold, Sum & Spread</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : (
          <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              <TabsTrigger value="gap" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-bold" data-testid="main-tab-gap">
                W37: Gap & Overdue
              </TabsTrigger>
              <TabsTrigger value="markov" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white font-bold" data-testid="main-tab-markov">
                W38: Markov Chain
              </TabsTrigger>
              <TabsTrigger value="hotcold" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white font-bold" data-testid="main-tab-hotcold">
                W39: Hot/Cold Cycles
              </TabsTrigger>
              <TabsTrigger value="sumspread" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-bold" data-testid="main-tab-sumspread">
                W40: Sum & Spread
              </TabsTrigger>
            </TabsList>

            <TabsContent value="gap">{gapData && <GapOverdueTab data={gapData} />}</TabsContent>
            <TabsContent value="markov">{markovData && <MarkovTab data={markovData} />}</TabsContent>
            <TabsContent value="hotcold">{hotColdData && <HotColdTab data={hotColdData} />}</TabsContent>
            <TabsContent value="sumspread">{sumSpreadData && <SumSpreadTab data={sumSpreadData} />}</TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
